import { type CreateProperty, type UpdateProperty } from '@flipdish/orgmanagement';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import type { NotifyProps } from '../../../layouts/Notify/actions';
import {
  type SyncPropertyDirectPhoneNumberArgs,
  PHONE_NUMBER_SYNC_FAILED_KEY,
  syncPropertyDirectPhoneNumber,
} from '../../../services/propertyPhoneNumber.service';
import { getAssociationsKey } from '../../RMS/rms.services';
import { createPropertyForOrg, updatePropertyById } from '../properties.services';

export interface PropertyMutationError {
  readonly message: string;
  readonly code: string;
}

export interface UsePropertyMutationsArgs {
  readonly orgId: string;
  readonly propertyId: string;
  readonly appId: string;
  /** False when the phone number is edited elsewhere, so an update must not write it to property-management. */
  readonly isPhoneNumberEditable?: boolean;
  readonly dispatchCloseNotifySaving: () => void;
  readonly dispatchNotifyError: (data: NotifyProps) => void;
  readonly dispatchNotifySaved: () => void;
}

export interface UsePropertyMutationsResult {
  readonly createPropertyMutation: UseMutationResult<
    unknown,
    PropertyMutationError,
    CreateProperty
  >;
  readonly updatePropertyMutation: UseMutationResult<
    unknown,
    PropertyMutationError,
    UpdateProperty
  >;
}

export const usePropertyMutations = (
  args: UsePropertyMutationsArgs
): UsePropertyMutationsResult => {
  const {
    orgId,
    propertyId,
    appId,
    isPhoneNumberEditable = true,
    dispatchCloseNotifySaving,
    dispatchNotifyError,
    dispatchNotifySaved,
  } = args;

  const history = useHistory();
  const queryClient = useQueryClient();

  // Runs only after the RMS save succeeded; a property-management failure warns but never fails the save.
  // A skipped write also warns: the property editor always has a property, so a skip means the number was lost.
  const syncPhoneNumber = async (args: Omit<SyncPropertyDirectPhoneNumberArgs, 'orgId'>) => {
    const result = await syncPropertyDirectPhoneNumber({ orgId, ...args });
    if (result !== 'synced') {
      dispatchNotifyError({ message: PHONE_NUMBER_SYNC_FAILED_KEY, translate: true });
    }
  };

  const createPropertyMutation = useMutation<unknown, PropertyMutationError, CreateProperty>({
    mutationFn: async (newProperty: CreateProperty) => {
      const response = await createPropertyForOrg(orgId, newProperty);
      if (newProperty.phoneNumber?.trim()) {
        await syncPhoneNumber({
          propertyId: response.data?.data?.propertyId,
          phoneNumber: newProperty.phoneNumber,
          source: 'property-create',
        });
      }
      return response;
    },
    onSuccess: () => {
      dispatchCloseNotifySaving();
      dispatchNotifySaved();
      queryClient.invalidateQueries({ queryKey: [getAssociationsKey] });
      history.push(`/${appId}/properties`);
    },
    onError: (error: PropertyMutationError) => {
      dispatchCloseNotifySaving();
      if (error.code === 'Property_name_already_exists') {
        dispatchNotifyError({ message: error.code, translate: true });
      } else {
        dispatchNotifyError({ message: 'Failed_to_create_property', translate: true });
      }
    },
  });

  const updatePropertyMutation = useMutation<unknown, PropertyMutationError, UpdateProperty>({
    mutationFn: async (updatedProperty: UpdateProperty) => {
      const response = await updatePropertyById(orgId, propertyId, updatedProperty);
      if (isPhoneNumberEditable && updatedProperty.phoneNumber !== undefined) {
        await syncPhoneNumber({
          propertyId,
          phoneNumber: updatedProperty.phoneNumber,
          source: 'property-edit',
        });
      }
      return response;
    },
    onSuccess: () => {
      dispatchCloseNotifySaving();
      dispatchNotifySaved();
      queryClient.invalidateQueries({ queryKey: [getAssociationsKey] });
      queryClient.invalidateQueries({ queryKey: ['getPropertyById'] });
    },
    onError: (error: PropertyMutationError) => {
      dispatchCloseNotifySaving();
      if (error.code === 'Property_name_already_exists') {
        dispatchNotifyError({ message: error.code, translate: true });
      } else {
        dispatchNotifyError({ message: 'Failed_to_update_property', translate: true });
      }
    },
  });

  return { createPropertyMutation, updatePropertyMutation };
};
